import type {
  Prefecture,
  Occupation,
  RecommendRecruitment,
  OccupationDetailsOccupationId,
} from '../../../types/recruitments';
import type { OccupationDetailCollection } from '../../organisms/recruitmentSearchForm/types';
import {
  FULL_TIME_WORKING_PERIOD_ID,
  SIDEBAR_PREFERENCE_CONDITION_NAMES,
} from '../../organisms/recruitmentSearchForm/types';
import type { SalaryChange } from '../../organisms/recruitmentSearchForm/types';
import React, { FunctionComponent, useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { Presentation } from './presentation';
import type { ApiState, MetaState, Props, SearchState, UiState } from './types';
import type { SearchHistories, SearchCondition, SaveConditionsResponse } from '../../../types/recruitments';
import { useDevice } from '../../../hooks/device';
import {
  getSearchFormOptions,
  getUser,
  searchRecruitments,
  searchSimilarRecruitments,
  fetchRecruitments,
  getUserPreferences,
  getSearchHistories,
  getSaveConditions,
  saveSearchConditions,
  deleteSaveConditions
} from '../../../services/recruitments';
import { getCsrfToken } from '../../../libs/csrfToken';
import { scrollToTop } from '../../../libs/scrollUtils';
import { useModal } from '../../../hooks/useModal';
import { getRecommendRecruitments } from '../../../services/people';

type ComponentProps = Omit<Props, 'state' | 'handlePaginateClick' | 'handleChangeQ' | 'handleSearch' | 'handleOpenSp' | 'handleFetchSort' | 'handleClick' | 'handleFetchOccupationDetails' | 'handleFetchPrefectureDetails' | 'checkAllOccupationDetails' | 'checkAllPrefectureDetails' | 'openList' | 'handlePopup' | 'handleModal' | 'closePopup' | 'handleChangeUrl' | 'renderSubContent'>;

const isRecruitmentSort = (value: string | null | undefined): value is 'popular' | 'new' => {
  return value === 'popular' || value === 'new';
};

const getSortFromLocation = () => {
  if (typeof location === 'undefined') return undefined;

  const sortParam = new URLSearchParams(location.search).get('sort');
  return isRecruitmentSort(sortParam) ? sortParam : undefined;
};

export const Component: FunctionComponent<ComponentProps> = ({
  user: initialUser,
  fromFixed,
  category,
  fixedName,
  ids,
  fixedParentName,
  parentIds,
  occupationDetailsOccupationIds: initialOccupationDetailsOccupationIds,
  fromStatic,
  occupationId,
  occupationDetailIds,
  recruitmentIds,
  fromSearchPage,
  h1Title,
  subContentTop,
  noCompanyIcon,
  noimage,
  hoverIcon,
  offline011Pc,
  offline011Sp,
  isStopped,
  isCompany,
  isPage,
  cancel_icon,
  cityId,
  cityIds,
  initial_employment_type,
  fromLp,
  isShowCompanyInfo,
  ...restProps
}) => {
  const { isPc, chromeEdg } = useDevice();
  const [sort, setSort] = useState<string>(() => getSortFromLocation() ?? (initialUser === 'person' ? 'new' : 'popular'));
  const [searchState, setSearchState] = useState<SearchState>({
    target_employment_types: [],
    target_occupations: [],
    target_occupation_details: [],
    target_prefectures: [],
    target_prefecture_details: [],
    target_prefecture_names: '',
    target_keywords: [],
    target_preference_conditions: [],
    target_working_periods: [],
    target_salary_type: '',
    target_salary_min: '',
    target_salary_max: '',
    q: '',
    occupationDetailsOccupationIds: [
      { occupation_id: 1, occupation_detail_id: [] },
      { occupation_id: 2, occupation_detail_id: [] },
      { occupation_id: 3, occupation_detail_id: [] },
      { occupation_id: 4, occupation_detail_id: [] },
      { occupation_id: 5, occupation_detail_id: [] },
      { occupation_id: 6, occupation_detail_id: [] },
      { occupation_id: 7, occupation_detail_id: [] },
      { occupation_id: 8, occupation_detail_id: [] },
      { occupation_id: 9, occupation_detail_id: [] },
      { occupation_id: 10, occupation_detail_id: [] },
      { occupation_id: 11, occupation_detail_id: [] },
      { occupation_id: 12, occupation_detail_id: [] },
      { occupation_id: 13, occupation_detail_id: [] }
    ],
    prefectureDetails: {
      '北海道・東北': [],
      関東: [],
      '北陸・甲信越': [],
      東海: [],
      近畿: [],
      '中国・四国': [],
      '九州・沖縄': []
    },
  });

  const [uiState, setUiState] = useState<UiState>({
    spStyle: { display: 'none' },
    toggleStyle: { left: '-800px' },
    toggleListStyle: { display: 'none' },
    popupStyle: { display: 'none' },
    modalStyle: { opacity: '0', visibility: 'hidden' } as React.CSSProperties,
    popupOpen: false,
    isSp: false,
    isPc: isPc,
    chromeEdg: chromeEdg,
    loading: false,
  });

  const [metaState, setMetaState] = useState<MetaState>({
    user: initialUser || 'without_login',
    page: 1,
    category: '',
    setDefault: 0,
    metaTitle: '',
    dataCategory: 'recruitmentlist',
    dataValueRecruitmentHeaderPc: {
      recruitmentsSort: 'recruitments_sort',
      new: 'new',
      popular: 'popular'
    },
    dataValueRecruitmentList: {
      moreViewRecruitment: 'more_view_recruitment',
      searchRecruitment: 'search_recruitment',
      recruitments: 'recruitments',
      newRecruitments: 'new_recruitments',
      newRecruitmentsScoutTag: 'new_recruitments_scout_tag',
      newRecrutimentsEntryTag: 'new_recruitments_entry_tag',
      newRecruitmentsAttentionTag: 'new_recruitments_attention_tag',
      newRecruitmentsPermanentTag: 'new_recruitments_permanent_tag',
      newRecruitmentsNewTag: 'new_recruitments_new_tag',
      newRecruitmentsActiveTag: 'new_recruitments_active_tag',
      newRecruitmentsHiringTag: 'new_recruitments_hiring_tag',
      newRecruitmentsAllTag: 'new_recruitments_all_tag',
      popularRecruitments: 'popular_recruitments',
      popularRecruitmentsScoutTag: 'popular_recruitments_scout_tag',
      popularRecruitmentsEntryTag: 'popular_recruitments_entry_tag',
      popularRecruitmentsAttentionTag: 'popular_recruitments_attention_tag',
      popularRecruitmentsPermanentTag: 'popular_recruitments_permanent_tag',
      popularRecruitmentsNewTag: 'popular_recruitments_new_tag',
      popularRecruitmentsActiveTag: 'popular_recruitments_active_tag',
      popularRecruitmentsHiringTag: 'popular_recruitments_hiring_tag',
      popularRecruitmentsAllTag: 'popular_recruitments_all_tag'
    },
    dataClickPoint: {
      company: 'company',
      image: 'image',
      title: 'title'
    }
  });

  const [apiState, setApiState] = useState<ApiState>({
    searchWords: [],
    recruitment_ids: [],
    similar_recruitment_ids: [],
    recruitment_employment_types: [],
    occupations: [],
    search_occupation_details: [],
    occupation_details: [],
    side_prefectures: [],
    all_prefectures: [],
    detail_prefectures: {},
    preference_condition_categories: []
  });

  const [shouldTriggerSearch, setShouldTriggerSearch] = useState(false);
  const [searchHistories, setSearchHistories] = useState<SearchHistories>({ conditions: [] });
  const [saveConditions, setSaveConditions] = useState<SearchCondition[]>([]);

  const [flash, setFlash] = useState(false);
  const [flashType, setFlashType] = useState('');
  const [flashMessage, setFlashMessage] = useState('');
  const [showNewGradBanner, setShowNewGradBanner] = useState(false);

  // lp経由の新規登録ユーザー向けの求人おすすめモーダル用
  const { isOpen: isModalOpen, open: handleModalOpen, close: handleModalClose } = useModal(false, {
    lockBody: true
  });
  const [recommendRecruitments, setRecommendRecruitments] = useState<RecommendRecruitment[]>([]);

  const categoryCountRef = useRef(0);
  const searchRequestIdRef = useRef(0);
  const similarSearchRequestIdRef = useRef(0);

  const fetchSimilarRecruitmentIds = useCallback(
    async (targetSearchState: SearchState, excludedRecruitmentIds: number[], sortValue: string) => {
      const requestId = ++similarSearchRequestIdRef.current;

      // 素の検索結果が20件を超える場合は類似求人を出さない（無駄なAPI呼び出しを避けるための早期リターン）。
      // 「どの検索条件なら類似を出すか」という業務ルールはサーバー側（Search::SimilarRecruitmentForm）に一本化している。
      if (excludedRecruitmentIds.length > 20) {
        setApiState(prev => ({ ...prev, similar_recruitment_ids: [] }));
        return;
      }

      try {
        const json = await searchSimilarRecruitments({
          csrfToken: getCsrfToken(),
          data: {
            employment_type: targetSearchState.target_employment_types,
            occupation_id: targetSearchState.target_occupations,
            occupation_detail_id: targetSearchState.target_occupation_details,
            prefecture_ids: targetSearchState.target_prefecture_details,
            q: targetSearchState.q,
            sort: sortValue,
            excluded_recruitment_ids: excludedRecruitmentIds
          }
        });
        if (requestId !== similarSearchRequestIdRef.current) return;

        setApiState(prev => ({
          ...prev,
          similar_recruitment_ids: json.recruitment_ids.map(Number)
        }));
      } catch (error) {
        if (requestId !== similarSearchRequestIdRef.current) return;

        console.error('Failed to search similar recruitments:', error);
        setApiState(prev => ({ ...prev, similar_recruitment_ids: [] }));
      }
    },
    []
  );

  const getActiveCityIds = () => {
    if (cityIds?.length) return cityIds;
    if (typeof location === 'undefined') return [];

    return new URLSearchParams(location.search).get('city_ids')?.split(',').filter(Boolean) ?? [];
  };

  useEffect(() => {
    const exTitle = document.title;

    setMetaState(prev => ({ ...prev, metaTitle: exTitle }));

    if (typeof window !== 'undefined' && (window.location.pathname.startsWith('/new_grad') || offline011Pc)) {
      setShowNewGradBanner(true);
    }

    getSearchFormOptionsData();
    // fetchSearchHistories();
    fetchSaveConditions();
    const initialSort = getSortFromLocation();
    if (initialSort) {
      setSort(initialSort);
    } else if (initialUser === 'person') {
      setSort('new');
      if (!fromFixed && location.search === '') {
        fetchUserParamsData();
      }
    } else {
      setSort('popular');
    }

    getUsersData();

    if (fromFixed) {
      const newState: Record<string, any> = {};
      const categoryValue = category;
      if (fixedName && ids) {
        newState[fixedName] = ids;
      }

      if (fixedParentName && fixedParentName.length > 0 && fixedParentName === 'target_occupations') {
        if (parentIds) {
          newState[fixedParentName] = parentIds;
        }

        if (initialOccupationDetailsOccupationIds) {
          newState['occupationDetailsOccupationIds'] = initialOccupationDetailsOccupationIds;
        }
      } else if (fixedParentName && fixedParentName.length > 0 && fixedParentName === 'target_prefectures') {
        const areaName = Array.isArray(parentIds) ? parentIds[0] : parentIds;
        const baseArray = { ...searchState.prefectureDetails };
        if (areaName && ids) {
          baseArray[areaName as keyof typeof baseArray] = ids;
        }
        newState.prefectureDetails = baseArray;
        if (areaName) {
          newState[fixedParentName] = [areaName];
        }

        if (fromStatic) {
          newState.target_occupations = occupationId ?? [];
          newState.target_occupation_details = occupationDetailIds ?? [];
          newState.occupationDetailsOccupationIds = initialOccupationDetailsOccupationIds ?? [];
        }
      }
      if (initial_employment_type) {
        newState.target_employment_types = [initial_employment_type];
      }
      flushSync(() => {
        setSearchState(prev => ({ ...prev, ...newState }));
      });
      if (recruitmentIds) {
        fetchSimilarRecruitmentIds(
          { ...searchState, ...newState },
          recruitmentIds.map(Number),
          initialSort || sort
        );
      }
      setMetaState(prev => ({ ...prev, category: categoryValue || '' }));
      fetchPageData();
    } else {
      fetchParamsData();
    }

    if ((initialUser === 'without_login' && !fromFixed) || initialUser === 'company') {
      if (location.search === '') {
        handleSearch('popular');
      }
      if (initialUser === 'without_login') {
        categoryCountRef.current += 1;
      }
    }

    if (recruitmentIds) {
      const rids = recruitmentIds;
      setApiState(prev => ({
        ...prev,
        recruitment_ids: rids.map(Number),
      }));
      setMetaState(prev => ({
        ...prev,
        setDefault: 1
      }));
    }
    setTimeout(() => {
      setShouldTriggerSearch(true);
    }, 0);
  }, []);

  useEffect(() => {
    setUiState(prev => ({
      ...prev,
      isPc: isPc,
      chromeEdg: chromeEdg
    }));
  }, [isPc, chromeEdg]);

  useEffect(() => {
    if (!fromLp) return;

    const fetchRecommend = async () => {
      try {
        const response = await getRecommendRecruitments();
        if (response.recommendRecruitments.length > 0) {
          setRecommendRecruitments(response.recommendRecruitments);
          handleModalOpen();
        }
      } catch (e) {
        console.error('Failed to fetch recommend recruitments', e);
      }
    };

    fetchRecommend();
  }, [fromLp]);

  const listRef = useRef<HTMLElement>(null);

  const handleFirstRecruitmentLoad = useCallback(() => {
    setUiState(prev => ({ ...prev, loading: false }));
  }, []);

  const handlePaginateClick = (page: number) => {
    if (page === metaState.page) return;
    setUiState(prev => ({ ...prev, loading: true }));
    scrollToTop(listRef.current, 28);
    setMetaState(prev => ({ ...prev, page: page }));
    if (fromFixed) {
      handleChangeUrl('from_button');
    }
  };

  const fetchParamsData = async () => {
    if (location.search !== '') {
      try {
        const json = await fetchRecruitments();
        const sessionStorageKey = `${location.href}`;
        const scrollValueItem = sessionStorage.getItem(sessionStorageKey);
        const scrollPage = scrollValueItem ? JSON.parse(scrollValueItem).page : 1;

        if (scrollPage !== json.page) {
          setMetaState(prev => ({ ...prev, page: scrollPage }));
        } else {
          setMetaState(prev => ({ ...prev, page: json.page }));
        }

        const fetchedSort = isRecruitmentSort(json.sort) ? json.sort : sort;
        const fetchedSearchState = {
          ...searchState,
          target_prefectures: json.target_prefectures,
          target_prefecture_details: json.target_prefecture_details,
          target_occupations: json.target_occupations,
          target_occupation_details: json.target_occupation_details,
          target_employment_types: json.target_employment_types,
          target_preference_conditions: json.target_preference_conditions || [],
          target_working_periods: json.target_working_periods || [],
          target_salary_type: json.target_salary_type || '',
          target_salary_min: json.target_salary_min || '',
          target_salary_max: json.target_salary_max || '',
          occupationDetailsOccupationIds: json.occupationDetailsOccupationIds,
          prefectureDetails: json.prefectureDetails,
          q: json.q,
        };

        setSort(fetchedSort);
        flushSync(() => {
          setSearchState(fetchedSearchState);
        });

        fetchSearchResults(fetchedSearchState, fetchedSort);
      } catch (error) {
        console.error('Failed to fetch params:', error);
      }
    } else {
      const sessionStorageKey = `${location.href}`;
      const scrollValueItem = sessionStorage.getItem(sessionStorageKey);
      const scrollPage = scrollValueItem ? JSON.parse(scrollValueItem).page : 1;
      const scrollPosition = scrollValueItem ? JSON.parse(scrollValueItem).value : 0;

      setMetaState(prev => ({ ...prev, page: scrollPage }));
      scrollStop();
      setTimeout(() => {
        scrollTo(0, scrollPosition);
        scrollRestart();
      }, 2000);
      sessionStorage.removeItem(sessionStorageKey);
    }
  };

  const scrollStop = () => {
    $('html, body').css('overflow', 'hidden');
    $(window).on('touchmove.noScroll', function (event: Event) {
      event.preventDefault();
    });
  };

  const scrollRestart = () => {
    $('html, body').css('overflow', 'visible');
    $(window).off('.noScroll');
  };

  const fetchPageData = async () => {
    if (location.search !== '') {
      try {
        const json = await fetchRecruitments();
        setMetaState(prev => ({ ...prev, page: json.page }));
      } catch (error) {
        console.error('Failed to fetch page:', error);
      }
    }
  };

  const fetchUserParamsData = async () => {
    try {
      const json = await getUserPreferences();
      setSearchState(prev => ({ ...prev, ...json }));
    } catch (error) {
      console.error('Failed to fetch user params:', error);
    }
  };

  const handleChange = (name: string, array: string[]) => {
    const newState: Record<string, any> = {};
    newState[name] = array.filter(Boolean);
    setSearchState(prev => ({ ...prev, ...newState }));
  };

  const handleChangeQ = (name: string, value: string) => {
    if (name === 'q') {
      const newState: Record<string, any> = {};
      newState[name] = value;
      setSearchState(prev => ({ ...prev, ...newState }));
    } else {
      setSearchState(prev => ({ ...prev, q: value }));
    }
  };

  const handleClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = $(event.currentTarget);
    const value = $(target).attr('value') as string;
    const name = $(target).attr('name') as string;
    const current = (searchState as any)[name] as string[];
    setMetaState(prev => ({ ...prev, page: 1 }));
    if (name === 'target_occupations') return; // handleFetchOccupationDetails、checkAllOccupationDetailsで実施
    if ($.inArray(value.split(',')[0], current) > -1) {
      const valueArray = value.split(',');
      const array = current.filter((i: string) => valueArray.indexOf(i) === -1);
      handleChange(name, array);
    } else {
      const array = current.concat(value.split(','));
      handleChange(name, array.filter((element, index) => array.indexOf(element) === index));
    }
  };


  const closePopup = () => {
    setUiState(prev => ({
      ...prev,
      popupStyle: { display: 'none' },
      popupOpen: false
    }));
  };


  const handleSaveConditions = async (title: string) => {
    try {
      await saveSearchConditions({
        csrfToken: getCsrfToken(),
        data: {
          employment_type: searchState.target_employment_types,
          occupation_id: searchState.target_occupations,
          occupation_detail_id: searchState.target_occupation_details,
          prefecture_ids: searchState.target_prefecture_details,
          preference_condition_id: searchState.target_preference_conditions,
          working_period_id: searchState.target_working_periods,
          salary_type_id: searchState.target_salary_type,
          salary_min: searchState.target_salary_min,
          salary_max: searchState.target_salary_max,
          q: searchState.q,
          sort: sort,
          title: title
        }
      });
      // await fetchSearchHistories();
      await fetchSaveConditions();
    } catch (error) {
      console.error('Failed to save search conditions:', error);
    }
  };


  const fetchSearchResults = async (targetSearchState: SearchState, sortValue: string) => {
    const requestId = ++searchRequestIdRef.current;
    similarSearchRequestIdRef.current += 1;
    setUiState(prev => ({ ...prev, loading: true }));
    setApiState(prev => ({ ...prev, similar_recruitment_ids: [] }));

    try {
      const json = await searchRecruitments({
        csrfToken: getCsrfToken(),
        data: {
          employment_type: targetSearchState.target_employment_types,
          occupation_id: targetSearchState.target_occupations,
          occupation_detail_id: targetSearchState.target_occupation_details,
          prefecture_ids: targetSearchState.target_prefecture_details,
          city_ids: getActiveCityIds(),
          keyword: targetSearchState.target_keywords,
          preference_condition_id: targetSearchState.target_preference_conditions,
          working_period_id: targetSearchState.target_working_periods,
          salary_type_id: targetSearchState.target_salary_type,
          salary_min: targetSearchState.target_salary_min,
          salary_max: targetSearchState.target_salary_max,
          q: targetSearchState.q,
          sort: sortValue
        }
      });
      if (requestId !== searchRequestIdRef.current) return;

      setMetaState(prev => ({
        ...prev,
        user: json.user,
      }));
      setApiState(prev => ({
        ...prev,
        recruitment_ids: json.recruitment_ids.map(Number)
      }));
      // await fetchSearchHistories();
      setUiState(prev => ({ ...prev, loading: false }));
      fetchSimilarRecruitmentIds(
        targetSearchState,
        json.recruitment_ids.map(Number),
        sortValue
      );

      return json;
    } catch (error) {
      console.error('Failed to search recruitments:', error);
      if (requestId === searchRequestIdRef.current) {
        setUiState(prev => ({ ...prev, loading: false }));
      }
    }
  };

  const handleSearch = async (sortArg: string) => {
    if (uiState.toggleStyle.left === '0px' && !uiState.isSp) {
      handleOpenSp();
    }

    const sortValue = sortArg.length > 0 ? sortArg : sort;
    const json = await fetchSearchResults(searchState, sortValue);
    if (!json) return;

    try {
      if (sortArg === 'for_paginate') {
        handleChangeUrl('from_searchForm');
      } else if (recruitmentIds && metaState.setDefault === 0) {
        const rids = recruitmentIds;
        setApiState(prev => ({
          ...prev,
          recruitment_ids: rids.map(Number),
        }));
        setMetaState(prev => ({
          ...prev,
          setDefault: 1
        }));
      } else {
        handleChangeUrl('from_handleSearch', sortArg);
      }
    } catch (error) {
      console.error('Failed to handle search result:', error);
    }
  };

  const buildSearchParams = (state: SearchState, sortValue: string, includeLogin: boolean) => {
    const searchParams = new URLSearchParams();
    const cityIdsForSearch = getActiveCityIds();
    if (state.q) searchParams.set('q', state.q);
    if (state.target_employment_types.length) searchParams.set('employment_type', state.target_employment_types.join(','));
    if (state.target_occupations.length) searchParams.set('occupation_id', state.target_occupations.join(','));
    if (state.target_occupation_details.length) searchParams.set('occupation_detail_ids', state.target_occupation_details.join(','));
    if (state.target_prefecture_details.length) searchParams.set('prefecture_ids', state.target_prefecture_details.join(','));
    if (cityIdsForSearch.length) searchParams.set('city_ids', cityIdsForSearch.join(','));
    if (state.target_preference_conditions.length) searchParams.set('preference_condition_ids', state.target_preference_conditions.join(','));
    if (state.target_working_periods.length) searchParams.set('working_period_ids', state.target_working_periods.join(','));
    if (state.target_salary_type) searchParams.set('salary_type_id', state.target_salary_type);
    if (state.target_salary_min) searchParams.set('salary_min', state.target_salary_min);
    if (state.target_salary_max) searchParams.set('salary_max', state.target_salary_max);
    if (state.target_keywords.length) searchParams.set('keyword', state.target_keywords.join(','));
    if (sortValue) searchParams.set('sort', sortValue);
    if (includeLogin) searchParams.set('status', 'login');
    return decodeURIComponent(searchParams.toString());
  };

  const handleChangeUrl = (fromSource: string, sortOverride?: string) => {  // sortのstate更新遅延の場合に対応するため引数でも渡せるように
    const baseUrl = `${location.protocol}//${location.host}`;
    let targetUrl = '';

    const sortValue =
      sortOverride === 'popular' || sortOverride === 'new'
        ? sortOverride
        : sort;

    if (fromFixed) {
      if (fromSource === 'from_handleSearch') {
        categoryCountRef.current += 1;
      }

      if (categoryCountRef.current > 0) {
        const queryString = buildSearchParams(searchState, sortValue, metaState.user !== 'without_login').toString();
        if (queryString) {
          targetUrl = `${baseUrl}/search?${queryString}`;
        } else if (sortValue !== '') {
          targetUrl = `${baseUrl}/search?sort=${sortValue}${
            metaState.user !== 'without_login' ? '&status=login' : ''
          }`;
        } else {
          targetUrl = `${baseUrl}/search${metaState.user !== 'without_login' ? '?status=login' : ''}`;
        }
        history.pushState({}, '', targetUrl);
      } else if (category === 'cities') {
        targetUrl = `${baseUrl}/${metaState.category}/${cityId}`;
        history.pushState({}, '', targetUrl);
      } else {
        targetUrl = `${baseUrl}/${metaState.category}`;
        history.pushState({}, '', targetUrl);
      }
    } else {
      if (categoryCountRef.current === 2 || categoryCountRef.current === 1) {
        const queryString = buildSearchParams(searchState, sortValue, metaState.user !== 'without_login').toString();
        if (queryString) {
          targetUrl = `${baseUrl}/search?${queryString}`;
        } else if (sortValue !== '') {
          targetUrl = `${baseUrl}/search?sort=${sortValue}${
            metaState.user !== 'without_login' ? '&status=login' : ''
          }`;
        } else {
          targetUrl = `${baseUrl}/search${metaState.user !== 'without_login' ? '?status=login' : ''}`;
        }
        history.pushState({}, '', targetUrl);
      } else {
        targetUrl = `${baseUrl}/search/${metaState.category}${metaState.user !== 'without_login' ? '?status=login' : ''}`;
        history.pushState({}, '', targetUrl);
        categoryCountRef.current += 1;
      }
    }

    if (fromFixed) {
      const urlWithPage = new URL(targetUrl);
      if (metaState.page !== 1) {
        urlWithPage.searchParams.set('page', String(metaState.page));
      } else {
        urlWithPage.searchParams.delete('page');
      }
      const finalUrl = urlWithPage.toString();
      if (metaState.page !== 1) {
        history.pushState({}, '', finalUrl);
        const currentTitle = metaState.metaTitle;
        const titleBase = currentTitle.replace(/｜READY TO FASHION/g, '');
        document.title = `${titleBase}（${metaState.page}ページ目）｜READY TO FASHION`;
      }
    }
  };

  const handleFetchOccupationDetails = (occupationId: string, occupationDetailId: string) => {
    const occIndex = Number(occupationId) - 1;

    setSearchState(prev => {
      const baseArray = [...prev.occupationDetailsOccupationIds];
      const currentDetails = [...prev.target_occupation_details];
      const isAlreadySelected = currentDetails.includes(occupationDetailId);

      let newDetailsForBlock = [...baseArray[occIndex].occupation_detail_id];
      if (isAlreadySelected) {
        newDetailsForBlock = newDetailsForBlock.filter(id => id !== occupationDetailId);
      } else {
        newDetailsForBlock = [...new Set([...newDetailsForBlock, occupationDetailId])];
      }
      baseArray[occIndex].occupation_detail_id = newDetailsForBlock;

      const newTargetDetails = isAlreadySelected
        ? currentDetails.filter(id => id !== occupationDetailId)
        : [...new Set([...currentDetails, occupationDetailId])];

      return {
        ...prev,
        occupationDetailsOccupationIds: baseArray,
        target_occupation_details: newTargetDetails,
      };
    });
  };

  const handleFetchPrefectureDetails = (areaName: string, prefectureId: string) => {
    const baseArray = { ...searchState.prefectureDetails };
    const current = searchState.prefectureDetails[areaName as keyof typeof searchState.prefectureDetails] || [];
    const prefectureCurrent = searchState.target_prefecture_details;

    if ($.inArray(prefectureId.split(',')[0], current) > -1) {
      const valueArray = prefectureId.split(',');
      const nameArray = areaName.split(',');
      const array = current.filter((i: string) => valueArray.indexOf(i) === -1);
      const prefectureArray = prefectureCurrent.filter((i: string) => prefectureId.indexOf(i) === -1);
      const targetPrefectures = searchState.target_prefectures;
      baseArray[areaName as keyof typeof baseArray] = array;
      setSearchState(prev => ({
        ...prev,
        prefectureDetails: baseArray,
        target_prefecture_details: prefectureArray,
        target_prefectures: targetPrefectures.filter((i: string) => nameArray.indexOf(i) === -1)
      }));
    } else {
      const array = current.concat(prefectureId.split(','));
      baseArray[areaName as keyof typeof baseArray] = array;
      setSearchState(prev => ({
        ...prev,
        target_prefectures: areaName.split(','),
        prefectureDetails: baseArray
      }));
    }
  };

  const checkAllOccupationDetails = (occupationId: string | number, checked: boolean) => {
    setSearchState(prev => {
      let newTargetOccs = [...prev.target_occupations];

      if (checked) {
        if (!newTargetOccs.includes(String(occupationId))) {
          newTargetOccs.push(String(occupationId));
        }
      } else {
        newTargetOccs = newTargetOccs.filter(id => id !== String(occupationId));
      }

      return {
        ...prev,
        target_occupations: newTargetOccs,
      };
    });
  };

  const checkAllPrefectureDetails = (array: string[], areaName: string, checked: boolean) => {
    const current = searchState.target_prefecture_details;
    const checkedPrefectureDetails = { ...searchState.prefectureDetails };

    if (checked) {
      const targetArray = current.concat(array);
      checkedPrefectureDetails[areaName as keyof typeof checkedPrefectureDetails] = array;
      setSearchState(prev => {
        const newTargetPrefectureDetails = targetArray.filter((element, index) => targetArray.indexOf(element) === index);
        return {
          ...prev,
          target_prefecture_details: newTargetPrefectureDetails,
          prefectureDetails: checkedPrefectureDetails
        };
      });
    } else {
      // ... (既存のロジック)
      if (areaName === '北海道・東北') {
        const duplicate = [...new Set([...current, ...['1', '2', '3', '4', '5', '6', '7']].filter(item => current.includes(item) && ['1', '2', '3', '4', '5', '6', '7'].includes(item)))];
        const newTargetPrefectureDetails = current.filter(item => !duplicate.includes(item));
        setSearchState(prev => ({ ...prev, target_prefecture_details: newTargetPrefectureDetails }));
      } else if (areaName === '関東') {
        const duplicate = [...new Set([...current, ...['8', '9', '10', '11', '12', '13', '14']].filter(item => current.includes(item) && ['8', '9', '10', '11', '12', '13', '14'].includes(item)))];
        const newTargetPrefectureDetails = current.filter(item => !duplicate.includes(item));
        setSearchState(prev => ({ ...prev, target_prefecture_details: newTargetPrefectureDetails }));
      } else if (areaName === '北陸・甲信越') {
        const duplicate = [...new Set([...current, ...['15', '16', '17', '18', '19', '20']].filter(item => current.includes(item) && ['15', '16', '17', '18', '19', '20'].includes(item)))];
        const newTargetPrefectureDetails = current.filter(item => !duplicate.includes(item));
        setSearchState(prev => ({ ...prev, target_prefecture_details: newTargetPrefectureDetails }));
      } else if (areaName === '東海') {
        const duplicate = [...new Set([...current, ...['21', '22', '23', '24']].filter(item => current.includes(item) && ['21', '22', '23', '24'].includes(item)))];
        const newTargetPrefectureDetails = current.filter(item => !duplicate.includes(item));
        setSearchState(prev => ({ ...prev, target_prefecture_details: newTargetPrefectureDetails }));
      } else if (areaName === '近畿') {
        const duplicate = [...new Set([...current, ...['25', '26', '27', '28', '29', '30']].filter(item => current.includes(item) && ['25', '26', '27', '28', '29', '30'].includes(item)))];
        const newTargetPrefectureDetails = current.filter(item => !duplicate.includes(item));
        setSearchState(prev => ({ ...prev, target_prefecture_details: newTargetPrefectureDetails }));
      } else if (areaName === '中国・四国') {
        const duplicate = [...new Set([...current, ...['31', '32', '33', '34', '35', '36', '37', '38', '39']].filter(item => current.includes(item) && ['31', '32', '33', '34', '35', '36', '37', '38', '39'].includes(item)))];
        const newTargetPrefectureDetails = current.filter(item => !duplicate.includes(item));
        setSearchState(prev => ({ ...prev, target_prefecture_details: newTargetPrefectureDetails }));
      } else if (areaName === '九州・沖縄') {
        const duplicate = [...new Set([...current, ...['40', '41', '42', '43', '44', '45', '46', '47']].filter(item => current.includes(item) && ['40', '41', '42', '43', '44', '45', '46', '47'].includes(item)))];
        const newTargetPrefectureDetails = current.filter(item => !duplicate.includes(item));
        setSearchState(prev => ({ ...prev, target_prefecture_details: newTargetPrefectureDetails }));
      }
      checkedPrefectureDetails[areaName as keyof typeof checkedPrefectureDetails] = [];
      setSearchState(prev => ({ ...prev, prefectureDetails: checkedPrefectureDetails }));
    }
  };

  const getUsersData = async () => {
    try {
      const json = await getUser();
      setMetaState(prev => ({ ...prev, user: json.user }));
    } catch (error) {
      console.error('Failed to get user:', error);
    }
  };

  const getSearchFormOptionsData = async () => {
    try {
      const json = await getSearchFormOptions();
      setApiState(prev => {
        const newState = {
          ...prev,
          recruitment_employment_types: json.recruitment_employment_types,
          occupations: json.occupations as Occupation[],
          search_occupation_details: json.search_occupation_details,
          occupation_details: json.occupation_details as unknown as OccupationDetailCollection[],
          side_prefectures: json.side_prefectures,
          all_prefectures: json.all_prefectures,
          detail_prefectures: json.detail_prefectures as unknown as { [key: string]: Prefecture[] },
          preference_condition_categories: json.preference_condition_categories || []
        };
        return newState;
      });
    } catch (error) {
      console.error('Failed to get search form options:', error);
    }
  };

  const fetchSearchHistories = async () => {
    try {
      const json = await getSearchHistories();
      setSearchHistories(json);
    } catch (error) {
      console.error('Failed to get search histories:', error);
    }
  };

  const fetchSaveConditions = async () => {
    try {
      const json = await getSaveConditions();
      setSaveConditions(json.saveConditions);
    } catch (error) {
      console.error('Failed to get save conditions:', error);
    }
  };

  const handleDeleteSaveConditions = async (conditionId: number | string) => {
    try {
      await deleteSaveConditions({ csrfToken: getCsrfToken(), conditionId });
      await fetchSaveConditions();
    } catch (error) {
      console.error('Failed to delete save conditions:', error);
    }
  };

  const handleFetchSort = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.currentTarget as HTMLElement;
    const value = target.dataset?.value;
    if (!value) return;
    const url = location.protocol + '//' + location.host;
    history.pushState({}, '', url);
    setSort(value);
    setMetaState(prev => ({
      ...prev,
      page: 1
    }));
    handleSearch(value);
  };

  const handleOpenSp = () => {
    if (!uiState.isSp) {
      setUiState(prev => ({
        ...prev,
        toggleStyle: { left: '0px' },
        isSp: true,
        spStyle: { display: 'block' }
      }));
      $('body').css({ height: '100dvh', overflow: 'hidden' });
    } else {
      setUiState(prev => ({
        ...prev,
        toggleStyle: { left: '-800px' },
        isSp: false,
        spStyle: { display: 'none' }
      }));
      $('body').css({ height: 'auto', overflow: 'unset' });
    }
  };

  const openList = (event: React.MouseEvent<HTMLLIElement>) => {
    const parent = event.currentTarget;
    const target = event.currentTarget.nextElementSibling as HTMLElement;
    $(parent).toggleClass('active');
    $(target).toggleClass('active');
  };

  const handlePopup = () => {
    if (uiState.popupStyle.display !== 'block') {
      setUiState(prev => ({
        ...prev,
        popupStyle: { display: 'block' },
        popupOpen: true
      }));
    } else {
      setUiState(prev => ({
        ...prev,
        popupStyle: { display: 'none' },
        popupOpen: false
      }));
    }
  };

  const handleModal = () => {
    if (uiState.modalStyle.opacity !== '1') {
      setUiState(prev => ({
        ...prev,
        modalStyle: { opacity: '1', visibility: 'visible' } as React.CSSProperties
      }));
    } else {
      setUiState(prev => ({
        ...prev,
        modalStyle: { opacity: '0', visibility: 'hidden' } as React.CSSProperties
      }));
    }
  };

  useEffect(() => {
    const isModalOpen = uiState.modalStyle.opacity === '1';
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [uiState.modalStyle]);

  const handleClearEmploymentTypes = useCallback(() => {
    setSearchState(prev => ({ ...prev, target_employment_types: [] }));
    setMetaState(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleSelectAllEmploymentTypes = useCallback(() => {
    setSearchState(prev => {
      const all = apiState.recruitment_employment_types || [];
      // 全選択済みなら state を変えず、冗長な再検索を防ぐ
      if (prev.target_employment_types.length === all.length) return prev;
      return { ...prev, target_employment_types: [...all] };
    });
    setMetaState(prev => ({ ...prev, page: 1 }));
  }, [apiState.recruitment_employment_types]);

  const handleClearOccupations = useCallback(() => {
    setSearchState(prev => ({
      ...prev,
      target_occupations: [],
      target_occupation_details: [],
      occupationDetailsOccupationIds: [
        { occupation_id: 1, occupation_detail_id: [] },
        { occupation_id: 2, occupation_detail_id: [] },
        { occupation_id: 3, occupation_detail_id: [] },
        { occupation_id: 4, occupation_detail_id: [] },
        { occupation_id: 5, occupation_detail_id: [] },
        { occupation_id: 6, occupation_detail_id: [] },
        { occupation_id: 7, occupation_detail_id: [] },
        { occupation_id: 8, occupation_detail_id: [] },
        { occupation_id: 9, occupation_detail_id: [] },
        { occupation_id: 10, occupation_detail_id: [] },
        { occupation_id: 11, occupation_detail_id: [] },
        { occupation_id: 12, occupation_detail_id: [] },
        { occupation_id: 13, occupation_detail_id: [] },
      ],
    }));
    setMetaState(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleClearLocations = useCallback(() => {
    setSearchState(prev => ({
      ...prev,
      target_prefectures: [],
      target_prefecture_details: [],
      target_prefecture_names: '',
      prefectureDetails: {
        '北海道・東北': [],
        関東: [],
        '北陸・甲信越': [],
        東海: [],
        近畿: [],
        '中国・四国': [],
        '九州・沖縄': [],
      },
    }));
    setMetaState(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleClearAreas = useCallback(() => {
    setSearchState(prev => ({
      ...prev,
      target_prefectures: [],
      // エリア選択は checkAllPrefectureDetails 経由で target_prefecture_details まで入るため一緒に空にする
      target_prefecture_details: [],
      target_prefecture_names: '',
      prefectureDetails: {
        '北海道・東北': [],
        関東: [],
        '北陸・甲信越': [],
        東海: [],
        近畿: [],
        '中国・四国': [],
        '九州・沖縄': [],
      },
    }));
    setMetaState(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleClearPrefectureDetails = useCallback(() => {
    setSearchState(prev => ({ ...prev, target_prefecture_details: [] }));
    setMetaState(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleChangeSalary = useCallback((change: SalaryChange) => {
    setSearchState(prev => ({ ...prev, ...change }));
    setMetaState(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleClearPreferenceConditions = useCallback(() => {
    setSearchState(prev => ({ ...prev, target_preference_conditions: [], target_working_periods: [] }));
    setMetaState(prev => ({ ...prev, page: 1 }));
  }, []);

  // サイドバーに表示している一部抽出の条件（正社員＋SIDEBAR_PREFERENCE_CONDITION_NAMES）をすべて選択する
  const handleSelectAllPreferenceConditions = useCallback(() => {
    const flatConditions = (apiState.preference_condition_categories || []).flatMap(c => c.preference_conditions);
    const sidebarIds = SIDEBAR_PREFERENCE_CONDITION_NAMES.flatMap(name => {
      const condition = flatConditions.find(pc => pc.name === name);
      return condition ? [String(condition.id)] : [];
    });
    setSearchState(prev => ({
      ...prev,
      target_preference_conditions: sidebarIds,
      target_working_periods: [FULL_TIME_WORKING_PERIOD_ID],
    }));
    setMetaState(prev => ({ ...prev, page: 1 }));
  }, [apiState.preference_condition_categories]);

  const handleSelectAllOccupations = useCallback(() => {
    setSearchState(prev => {
      const all = (apiState.occupations || []).map(o => String(o.id));
      // 全選択済みなら state を変えず、冗長な再検索を防ぐ
      if (prev.target_occupations.length === all.length) return prev;
      return { ...prev, target_occupations: all };
    });
    setMetaState(prev => ({ ...prev, page: 1 }));
  }, [apiState.occupations]);

  const handleSelectAllLocations = useCallback(() => {
    setSearchState(prev => {
      const all = (apiState.all_prefectures || []).map(p => String(p.id));
      if (prev.target_prefecture_details.length === all.length) return prev;
      return {
        ...prev,
        target_prefecture_details: all,
        prefectureDetails: {
          '北海道・東北': ['1', '2', '3', '4', '5', '6', '7'],
          関東: ['8', '9', '10', '11', '12', '13', '14'],
          '北陸・甲信越': ['15', '16', '17', '18', '19', '20'],
          東海: ['21', '22', '23', '24'],
          近畿: ['25', '26', '27', '28', '29', '30'],
          '中国・四国': ['31', '32', '33', '34', '35', '36', '37', '38', '39'],
          '九州・沖縄': ['40', '41', '42', '43', '44', '45', '46', '47'],
        },
      };
    });
    setMetaState(prev => ({ ...prev, page: 1 }));
  }, [apiState.all_prefectures]);

  const handleClearAll = useCallback(() => {
    setSearchState(() => {
      const newSearchState = {
        target_employment_types: [],
        target_occupations: [],
        target_occupation_details: [],
        target_prefectures: [],
        target_prefecture_details: [],
        target_prefecture_names: '',
        target_keywords: [],
        target_preference_conditions: [],
        target_working_periods: [],
        target_salary_type: '',
        target_salary_min: '',
        target_salary_max: '',
            q: '',
        occupationDetailsOccupationIds: [
          { occupation_id: 1, occupation_detail_id: [] },
          { occupation_id: 2, occupation_detail_id: [] },
          { occupation_id: 3, occupation_detail_id: [] },
          { occupation_id: 4, occupation_detail_id: [] },
          { occupation_id: 5, occupation_detail_id: [] },
          { occupation_id: 6, occupation_detail_id: [] },
          { occupation_id: 7, occupation_detail_id: [] },
          { occupation_id: 8, occupation_detail_id: [] },
          { occupation_id: 9, occupation_detail_id: [] },
          { occupation_id: 10, occupation_detail_id: [] },
          { occupation_id: 11, occupation_detail_id: [] },
          { occupation_id: 12, occupation_detail_id: [] },
          { occupation_id: 13, occupation_detail_id: [] }
        ],
        prefectureDetails: {
          '北海道・東北': [],
          関東: [],
          '北陸・甲信越': [],
          東海: [],
          近畿: [],
          '中国・四国': [],
          '九州・沖縄': []
        },
      };
      return newSearchState;
    });
    setMetaState(prev => ({ ...prev, page: 1 }));
  }, [setSearchState, setMetaState]);

  const handleApplySearchHistory = useCallback((condition: SearchCondition) => {
    // prefectureDetailsを再構築
    const newPrefectureDetails = {
      '北海道・東北': [] as string[],
      関東: [] as string[],
      '北陸・甲信越': [] as string[],
      東海: [] as string[],
      近畿: [] as string[],
      '中国・四国': [] as string[],
      '九州・沖縄': [] as string[]
    };

    condition.target_prefecture_details.forEach((prefId) => {
      const id = String(prefId);
      if (['1', '2', '3', '4', '5', '6', '7'].includes(id)) {
        newPrefectureDetails['北海道・東北'].push(id);
      } else if (['8', '9', '10', '11', '12', '13', '14'].includes(id)) {
        newPrefectureDetails['関東'].push(id);
      } else if (['15', '16', '17', '18', '19', '20'].includes(id)) {
        newPrefectureDetails['北陸・甲信越'].push(id);
      } else if (['21', '22', '23', '24'].includes(id)) {
        newPrefectureDetails['東海'].push(id);
      } else if (['25', '26', '27', '28', '29', '30'].includes(id)) {
        newPrefectureDetails['近畿'].push(id);
      } else if (['31', '32', '33', '34', '35', '36', '37', '38', '39'].includes(id)) {
        newPrefectureDetails['中国・四国'].push(id);
      } else if (['40', '41', '42', '43', '44', '45', '46', '47'].includes(id)) {
        newPrefectureDetails['九州・沖縄'].push(id);
      }
    });

    // occupationDetailsOccupationIdsを再構築
    const newOccupationDetailsOccupationIds: OccupationDetailsOccupationId[] = [
      { occupation_id: 1, occupation_detail_id: [] },
      { occupation_id: 2, occupation_detail_id: [] },
      { occupation_id: 3, occupation_detail_id: [] },
      { occupation_id: 4, occupation_detail_id: [] },
      { occupation_id: 5, occupation_detail_id: [] },
      { occupation_id: 6, occupation_detail_id: [] },
      { occupation_id: 7, occupation_detail_id: [] },
      { occupation_id: 8, occupation_detail_id: [] },
      { occupation_id: 9, occupation_detail_id: [] },
      { occupation_id: 10, occupation_detail_id: [] },
      { occupation_id: 11, occupation_detail_id: [] },
      { occupation_id: 12, occupation_detail_id: [] },
      { occupation_id: 13, occupation_detail_id: [] }
    ];

    // occupation_idから全ての小カテゴリを設定
    condition.target_occupations.forEach((occId) => {
      const occIndex = Number(occId) - 1;
      if (occIndex >= 0 && occIndex < 13) {
        const occBlock = apiState.occupation_details.find(
          (o: any) => o.occupation_id === Number(occId)
        );
        const allDetailIds = occBlock?.occupation_details.map((od: any) => String(od.id)) || [];
        newOccupationDetailsOccupationIds[occIndex].occupation_detail_id = allDetailIds;
      }
    });

    // occupation_detail_idから個別に設定
    condition.target_occupation_details.forEach((detailId) => {
      const occBlock = apiState.occupation_details.find(
        (o: any) => o.occupation_details.some((od: any) => String(od.id) === String(detailId))
      );
      if (occBlock) {
        const occIndex = occBlock.occupation_id - 1;
        if (!newOccupationDetailsOccupationIds[occIndex].occupation_detail_id.includes(String(detailId))) {
          newOccupationDetailsOccupationIds[occIndex].occupation_detail_id.push(String(detailId));
        }
      }
    });

    // target_prefecturesを計算
    const newTargetPrefectures: string[] = [];
    Object.entries(newPrefectureDetails).forEach(([area, prefIds]) => {
      if (prefIds.length > 0) {
        newTargetPrefectures.push(area);
      }
    });

    setSearchState({
      target_employment_types: condition.target_employment_types.map(String),
      target_occupations: condition.target_occupations.map(String),
      target_occupation_details: condition.target_occupation_details.map(String),
      target_prefectures: newTargetPrefectures,
      target_prefecture_details: condition.target_prefecture_details.map(String),
      target_prefecture_names: '',
      target_preference_conditions: (condition.target_preference_conditions || []).map(String),
      target_working_periods: (condition.target_working_periods || []).map(String),
      target_salary_type: condition.target_salary_type || '',
      target_salary_min: condition.target_salary_min || '',
      target_salary_max: condition.target_salary_max || '',
        q: condition.q || '',
      target_keywords: [],
      occupationDetailsOccupationIds: newOccupationDetailsOccupationIds,
      prefectureDetails: newPrefectureDetails
    });

    setMetaState(prev => ({ ...prev, page: 1 }));
  }, [apiState.occupation_details, setSearchState, setMetaState]);

  const renderSubContent = () => {
    return (
      <section className="search-form__bottom">
        <div className="articles-container-text">
          <div className="articles-container-text-description" dangerouslySetInnerHTML={{ __html: subContentTop || '' }} />
        </div>
      </section>
    );
  };

  const handleFlash = (flashValue: boolean, type: string, message: string) => {
    setFlash(flashValue);
    setFlashType(type);
    setFlashMessage(message);
  };

  return (
    <Presentation
      {...restProps}
      shouldTriggerSearch={shouldTriggerSearch}
      user={metaState.user}
      h1Title={h1Title}
      fromFixed={fromFixed}
      fromSearchPage={fromSearchPage}
      subContentTop={subContentTop}
      noCompanyIcon={noCompanyIcon}
      noimage={noimage}
      hoverIcon={hoverIcon}
      offline011Pc={offline011Pc}
      offline011Sp={offline011Sp}
      isStopped={isStopped}
      isCompany={isCompany}
      isPage={isPage}
      recruitmentIds={recruitmentIds}
      cancel_icon={cancel_icon}
      searchState={searchState}
      uiState={uiState}
      metaState={metaState}
      apiState={apiState}
      sort={sort}
      handlePaginateClick={handlePaginateClick}
      handleChangeQ={handleChangeQ}
      handleSearch={handleSearch}
      handleOpenSp={handleOpenSp}
      handleFetchSort={handleFetchSort}
      handleClick={handleClick}
      handleFetchOccupationDetails={handleFetchOccupationDetails}
      handleFetchPrefectureDetails={handleFetchPrefectureDetails}
      checkAllOccupationDetails={checkAllOccupationDetails}
      checkAllPrefectureDetails={checkAllPrefectureDetails}
      openList={openList}
      handlePopup={handlePopup}
      handleModal={handleModal}
      closePopup={closePopup}
      handleChangeUrl={handleChangeUrl}
      renderSubContent={renderSubContent}
      onFirstLoad={handleFirstRecruitmentLoad}
      listRef={listRef}
      handleClearAll={handleClearAll}
      handleClearEmploymentTypes={handleClearEmploymentTypes}
      handleSelectAllEmploymentTypes={handleSelectAllEmploymentTypes}
      handleClearOccupations={handleClearOccupations}
      handleSelectAllOccupations={handleSelectAllOccupations}
      handleClearLocations={handleClearLocations}
      handleSelectAllLocations={handleSelectAllLocations}
      handleClearPreferenceConditions={handleClearPreferenceConditions}
      handleSelectAllPreferenceConditions={handleSelectAllPreferenceConditions}
      handleChangeSalary={handleChangeSalary}
      handleClearAreas={handleClearAreas}
      handleClearPrefectureDetails={handleClearPrefectureDetails}
      searchHistories={searchHistories}
      handleApplySearchHistory={handleApplySearchHistory}
      handleSaveConditions={handleSaveConditions}
      saveConditions={saveConditions}
      handleDeleteSaveConditions={handleDeleteSaveConditions}
      flash={flash}
      flashType={flashType}
      flashMessage={flashMessage}
      handleFlash={handleFlash}
      showNewGradBanner={showNewGradBanner}
      isShowCompanyInfo={isShowCompanyInfo}
      isModalOpen={isModalOpen}
      handleModalClose={handleModalClose}
      recommendRecruitments={recommendRecruitments}
    />
  );
};
